/**
 * Tank Calculations Utility
 *
 * مكتبة شاملة لحساب جميع القيم المتعلقة بالخزانات:
 * - حساب الحجم من الأبعاد
 * - تحويلات الوحدات
 * - حساب النسب المئوية
 * - حساب الكميات المتبقية
 */

// ═══════════════════════════════════════════════════════════
// Constants - الثوابت
// ═══════════════════════════════════════════════════════════

const PI = Math.PI;

// معاملات التحويل
export const CONVERSION_FACTORS = {
  // حجم
  LITERS_TO_GALLONS: 0.264172, // لتر إلى جالون أمريكي
  LITERS_TO_M3: 0.001, // لتر إلى متر مكعب
  GALLONS_TO_LITERS: 3.78541, // جالون إلى لتر
  M3_TO_LITERS: 1000, // متر مكعب إلى لتر
  CM3_TO_LITERS: 0.001, // سم مكعب إلى لتر

  // طول
  CM_TO_METERS: 0.01, // سنتيمتر إلى متر
  METERS_TO_CM: 100, // متر إلى سنتيمتر

  // انبعاثات (للحسابات البيئية المستقبلية)
  DIESEL_CO2_PER_LITER: 2.68, // كجم CO2 لكل لتر ديزل
  GASOLINE_CO2_PER_LITER: 2.31, // كجم CO2 لكل لتر بنزين
} as const;

// ═══════════════════════════════════════════════════════════
// Type Definitions - تعريفات الأنواع
// ═══════════════════════════════════════════════════════════

export type TankShape = 'cylindrical' | 'rectangular' | 'spherical' | 'custom';
export type VolumeUnit = 'L' | 'm3' | 'gal';

export interface TankDimensions {
  heightCm?: number;
  diameterCm?: number; // للأسطواني والكروي
  radiusCm?: number; // بديل للقطر
  lengthCm?: number; // للمستطيل
  widthCm?: number; // للمستطيل
}

export interface TankConfig {
  shape: TankShape;
  dimensions: TankDimensions;
  capacityLiters?: number; // يمكن حسابها أو إدخالها يدوياً
  unit?: VolumeUnit;
}

export interface TankStatus {
  currentLevelCm: number;
  capacityLiters: number;
  heightCm: number;
  percentageFull: number;
  remainingLiters: number;
  remainingGallons: number;
  remainingM3: number;
  usedLiters: number;
  usedPercentage: number;
}

export interface RefillCalculation {
  currentPercentage: number;
  targetPercentage: number;
  requiredLiters: number;
  requiredGallons: number;
  estimatedCost?: number;
  pricePerLiter?: number;
}

// ═══════════════════════════════════════════════════════════
// Volume Calculations - حسابات الحجم
// ═══════════════════════════════════════════════════════════

/**
 * حساب حجم خزان أسطواني
 * الصيغة: V = π × r² × h
 *
 * @param heightCm - الارتفاع بالسنتيمتر
 * @param diameterCm - القطر بالسنتيمتر
 * @returns الحجم باللتر
 */
export function calculateCylindricalVolume(heightCm: number, diameterCm: number): number {
  if (heightCm <= 0 || diameterCm <= 0) {
    throw new Error('Height and diameter must be positive numbers');
  }

  const radiusCm = diameterCm / 2;
  const volumeCm3 = PI * Math.pow(radiusCm, 2) * heightCm;
  const volumeLiters = volumeCm3 * CONVERSION_FACTORS.CM3_TO_LITERS;

  return Math.round(volumeLiters * 100) / 100; // تzaround إلى رقمين عشريين
}

/**
 * حساب حجم خزان مستطيل
 * الصيغة: V = length × width × height
 *
 * @param lengthCm - الطول بالسنتيمتر
 * @param widthCm - العرض بالسنتيمتر
 * @param heightCm - الارتفاع بالسنتيمتر
 * @returns الحجم باللتر
 */
export function calculateRectangularVolume(
  lengthCm: number,
  widthCm: number,
  heightCm: number,
): number {
  if (lengthCm <= 0 || widthCm <= 0 || heightCm <= 0) {
    throw new Error('All dimensions must be positive numbers');
  }

  const volumeCm3 = lengthCm * widthCm * heightCm;
  const volumeLiters = volumeCm3 * CONVERSION_FACTORS.CM3_TO_LITERS;

  return Math.round(volumeLiters * 100) / 100;
}

/**
 * حساب حجم خزان كروي
 * الصيغة: V = (4/3) × π × r³
 *
 * @param diameterCm - القطر بالسنتيمتر
 * @returns الحجم باللتر
 */
export function calculateSphericalVolume(diameterCm: number): number {
  if (diameterCm <= 0) {
    throw new Error('Diameter must be a positive number');
  }

  const radiusCm = diameterCm / 2;
  const volumeCm3 = (4 / 3) * PI * Math.pow(radiusCm, 3);
  const volumeLiters = volumeCm3 * CONVERSION_FACTORS.CM3_TO_LITERS;

  return Math.round(volumeLiters * 100) / 100;
}

/**
 * حساب حجم الخزان بناءً على الشكل
 *
 * @param config - إعدادات الخزان
 * @returns الحجم باللتر
 */
export function calculateTankVolume(config: TankConfig): number {
  const { shape, dimensions, capacityLiters } = config;

  // إذا كانت السعة محددة يدوياً (custom)
  if (shape === 'custom' && capacityLiters) {
    return capacityLiters;
  }

  switch (shape) {
    case 'cylindrical':
      if (!dimensions.heightCm || !dimensions.diameterCm) {
        throw new Error('Cylindrical tank requires height and diameter');
      }
      return calculateCylindricalVolume(dimensions.heightCm, dimensions.diameterCm);

    case 'rectangular':
      if (!dimensions.lengthCm || !dimensions.widthCm || !dimensions.heightCm) {
        throw new Error('Rectangular tank requires length, width, and height');
      }
      return calculateRectangularVolume(
        dimensions.lengthCm,
        dimensions.widthCm,
        dimensions.heightCm,
      );

    case 'spherical':
      if (!dimensions.diameterCm) {
        throw new Error('Spherical tank requires diameter');
      }
      return calculateSphericalVolume(dimensions.diameterCm);

    default:
      throw new Error(`Unknown tank shape: ${shape}`);
  }
}

// ═══════════════════════════════════════════════════════════
// Unit Conversions - تحويلات الوحدات
// ═══════════════════════════════════════════════════════════

/**
 * تحويل من لتر إلى جالون
 */
export function litersToGallons(liters: number): number {
  return Math.round(liters * CONVERSION_FACTORS.LITERS_TO_GALLONS * 100) / 100;
}

/**
 * تحويل من لتر إلى متر مكعب
 */
export function litersToM3(liters: number): number {
  return Math.round(liters * CONVERSION_FACTORS.LITERS_TO_M3 * 1000) / 1000;
}

/**
 * تحويل من جالون إلى لتر
 */
export function gallonsToLiters(gallons: number): number {
  return Math.round(gallons * CONVERSION_FACTORS.GALLONS_TO_LITERS * 100) / 100;
}

/**
 * تحويل من متر مكعب إلى لتر
 */
export function m3ToLiters(m3: number): number {
  return Math.round(m3 * CONVERSION_FACTORS.M3_TO_LITERS * 100) / 100;
}

/**
 * تحويل الحجم إلى الوحدة المطلوبة
 */
export function convertVolume(liters: number, targetUnit: VolumeUnit): number {
  switch (targetUnit) {
    case 'L':
      return liters;
    case 'gal':
      return litersToGallons(liters);
    case 'm3':
      return litersToM3(liters);
    default:
      return liters;
  }
}

// ═══════════════════════════════════════════════════════════
// Percentage & Level Calculations - حسابات النسبة والمستوى
// ═══════════════════════════════════════════════════════════

/**
 * حساب النسبة المئوية للامتلاء من الارتفاع الحالي
 *
 * @param currentLevelCm - المستوى الحالي بالسنتيمتر
 * @param tankHeightCm - الارتفاع الكلي للخزان بالسنتيمتر
 * @returns النسبة المئوية (0-100)
 */
export function calculatePercentage(currentLevelCm: number, tankHeightCm: number): number {
  if (tankHeightCm <= 0) {
    throw new Error('Tank height must be positive');
  }

  if (currentLevelCm < 0) {
    return 0;
  }

  if (currentLevelCm > tankHeightCm) {
    return 100; // الحد الأقصى 100%
  }

  const percentage = (currentLevelCm / tankHeightCm) * 100;
  return Math.round(percentage * 10) / 10; // رقم عشري واحد
}

/**
 * حساب الارتفاع من النسبة المئوية
 */
export function calculateLevelFromPercentage(percentage: number, tankHeightCm: number): number {
  if (percentage < 0 || percentage > 100) {
    throw new Error('Percentage must be between 0 and 100');
  }

  const levelCm = (percentage / 100) * tankHeightCm;
  return Math.round(levelCm * 10) / 10;
}

/**
 * حساب الحجم المتبقي من المستوى الحالي
 *
 * @param currentLevelCm - المستوى الحالي بالسنتيمتر
 * @param tankHeightCm - الارتفاع الكلي للخزان
 * @param tankCapacityLiters - السعة الكلية باللتر
 * @returns الحجم المتبقي باللتر
 */
export function calculateRemainingVolume(
  currentLevelCm: number,
  tankHeightCm: number,
  tankCapacityLiters: number,
): number {
  const percentage = calculatePercentage(currentLevelCm, tankHeightCm);
  const remainingLiters = (percentage / 100) * tankCapacityLiters;
  return Math.round(remainingLiters * 100) / 100;
}

/**
 * حساب الحجم المستخدم (المفرغ)
 */
export function calculateUsedVolume(
  currentLevelCm: number,
  tankHeightCm: number,
  tankCapacityLiters: number,
): number {
  const remaining = calculateRemainingVolume(currentLevelCm, tankHeightCm, tankCapacityLiters);
  return Math.round((tankCapacityLiters - remaining) * 100) / 100;
}

/**
 * الحصول على حالة كاملة للخزان
 */
export function getTankStatus(
  currentLevelCm: number,
  capacityLiters: number,
  heightCm: number,
): TankStatus {
  const percentageFull = calculatePercentage(currentLevelCm, heightCm);
  const remainingLiters = calculateRemainingVolume(currentLevelCm, heightCm, capacityLiters);
  const usedLiters = calculateUsedVolume(currentLevelCm, heightCm, capacityLiters);

  return {
    currentLevelCm,
    capacityLiters,
    heightCm,
    percentageFull,
    remainingLiters,
    remainingGallons: litersToGallons(remainingLiters),
    remainingM3: litersToM3(remainingLiters),
    usedLiters,
    usedPercentage: Math.round((100 - percentageFull) * 10) / 10,
  };
}

// ═══════════════════════════════════════════════════════════
// Refill Calculations - حسابات التعبئة
// ═══════════════════════════════════════════════════════════

/**
 * حساب كمية التعبئة المطلوبة
 *
 * @param currentPercentage - النسبة الحالية
 * @param targetPercentage - النسبة المستهدفة
 * @param tankCapacityLiters - السعة الكلية
 * @param pricePerLiter - سعر اللتر (اختياري)
 * @returns بيانات التعبئة
 */
export function calculateRefillAmount(
  currentPercentage: number,
  targetPercentage: number,
  tankCapacityLiters: number,
  pricePerLiter?: number,
): RefillCalculation {
  if (targetPercentage < currentPercentage) {
    throw new Error('Target percentage must be greater than current percentage');
  }

  if (targetPercentage > 100) {
    targetPercentage = 100;
  }

  const percentageDiff = targetPercentage - currentPercentage;
  const requiredLiters = (percentageDiff / 100) * tankCapacityLiters;
  const requiredGallons = litersToGallons(requiredLiters);

  const result: RefillCalculation = {
    currentPercentage,
    targetPercentage,
    requiredLiters: Math.round(requiredLiters * 100) / 100,
    requiredGallons: Math.round(requiredGallons * 100) / 100,
  };

  if (pricePerLiter) {
    result.pricePerLiter = pricePerLiter;
    result.estimatedCost = Math.round(requiredLiters * pricePerLiter * 100) / 100;
  }

  return result;
}

/**
 * حساب كمية التعبئة بناءً على الحجم المطلوب
 */
export function calculateRefillToVolume(
  currentLevelCm: number,
  targetVolumeLiters: number,
  tankHeightCm: number,
  tankCapacityLiters: number,
  pricePerLiter?: number,
): RefillCalculation {
  const currentPercentage = calculatePercentage(currentLevelCm, tankHeightCm);
  const targetPercentage = (targetVolumeLiters / tankCapacityLiters) * 100;

  return calculateRefillAmount(
    currentPercentage,
    targetPercentage,
    tankCapacityLiters,
    pricePerLiter,
  );
}

// ═══════════════════════════════════════════════════════════
// Consumption & Prediction - الاستهلاك والتوقع
// ═══════════════════════════════════════════════════════════

/**
 * حساب معدل الاستهلاك اليومي
 *
 * @param readings - قائمة القراءات (levelCm, timestamp)
 * @returns معدل الاستهلاك باللتر/يوم
 */
export function calculateDailyConsumption(
  readings: Array<{ levelCm: number; timestamp: Date }>,
  tankHeightCm: number,
  tankCapacityLiters: number,
): number {
  if (readings.length < 2) {
    return 0;
  }

  // ترتيب القراءات حسب التاريخ
  const sortedReadings = [...readings].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );

  const firstReading = sortedReadings[0];
  const lastReading = sortedReadings[sortedReadings.length - 1];

  // حساب الفرق في الحجم
  const firstVolume = calculateRemainingVolume(
    firstReading.levelCm,
    tankHeightCm,
    tankCapacityLiters,
  );
  const lastVolume = calculateRemainingVolume(
    lastReading.levelCm,
    tankHeightCm,
    tankCapacityLiters,
  );

  const volumeDiff = firstVolume - lastVolume; // الكمية المستهلكة

  // حساب عدد الأيام
  const timeDiffMs = lastReading.timestamp.getTime() - firstReading.timestamp.getTime();
  const daysDiff = timeDiffMs / (1000 * 60 * 60 * 24);

  if (daysDiff === 0) {
    return 0;
  }

  const dailyConsumption = volumeDiff / daysDiff;
  return Math.max(0, Math.round(dailyConsumption * 100) / 100); // لا يمكن أن يكون سالباً
}

/**
 * توقع عدد الأيام المتبقية حتى النفاد
 *
 * @param currentVolumeLiters - الحجم الحالي
 * @param dailyConsumptionLiters - معدل الاستهلاك اليومي
 * @returns عدد الأيام المتبقية
 */
export function predictDaysUntilEmpty(
  currentVolumeLiters: number,
  dailyConsumptionLiters: number,
): number {
  if (dailyConsumptionLiters <= 0) {
    return Infinity; // لا استهلاك = لا نفاد
  }

  const daysRemaining = currentVolumeLiters / dailyConsumptionLiters;
  return Math.round(daysRemaining * 10) / 10;
}

/**
 * توقع تاريخ النفاد
 */
export function predictEmptyDate(
  currentVolumeLiters: number,
  dailyConsumptionLiters: number,
): Date | null {
  const daysRemaining = predictDaysUntilEmpty(currentVolumeLiters, dailyConsumptionLiters);

  if (!isFinite(daysRemaining)) {
    return null;
  }

  const emptyDate = new Date();
  emptyDate.setDate(emptyDate.getDate() + daysRemaining);
  return emptyDate;
}

// ═══════════════════════════════════════════════════════════
// Validation - التحقق من الصحة
// ═══════════════════════════════════════════════════════════

/**
 * التحقق من صحة أبعاد الخزان
 */
export function validateTankDimensions(dimensions: TankDimensions, shape: TankShape): boolean {
  switch (shape) {
    case 'cylindrical':
      return !!(
        dimensions.heightCm &&
        dimensions.heightCm > 0 &&
        dimensions.diameterCm &&
        dimensions.diameterCm > 0
      );

    case 'rectangular':
      return !!(
        dimensions.lengthCm &&
        dimensions.lengthCm > 0 &&
        dimensions.widthCm &&
        dimensions.widthCm > 0 &&
        dimensions.heightCm &&
        dimensions.heightCm > 0
      );

    case 'spherical':
      return !!(dimensions.diameterCm && dimensions.diameterCm > 0);

    case 'custom':
      return true; // لا يتطلب أبعاد

    default:
      return false;
  }
}

/**
 * التحقق من معقولية القراءة
 */
export function validateReading(
  levelCm: number,
  tankHeightCm: number,
): { valid: boolean; error?: string } {
  if (levelCm < 0) {
    return { valid: false, error: 'المستوى لا يمكن أن يكون سالباً' };
  }

  if (levelCm > tankHeightCm * 1.1) {
    // هامش 10% للخطأ
    return { valid: false, error: 'المستوى يتجاوز ارتفاع الخزان بشكل غير معقول' };
  }

  return { valid: true };
}

// ═══════════════════════════════════════════════════════════
// Formatting Helpers - مساعدات التنسيق
// ═══════════════════════════════════════════════════════════

/**
 * تنسيق الحجم مع الوحدة
 */
export function formatVolume(liters: number, unit: VolumeUnit, decimals: number = 2): string {
  const value = convertVolume(liters, unit);
  const formatted = value.toFixed(decimals);

  const unitSymbols = {
    L: 'L',
    gal: 'gal',
    m3: 'm³',
  };

  return `${formatted} ${unitSymbols[unit]}`;
}

/**
 * تنسيق النسبة المئوية
 */
export function formatPercentage(percentage: number, decimals: number = 1): string {
  return `${percentage.toFixed(decimals)}%`;
}

/**
 * تنسيق التكلفة
 */
export function formatCost(cost: number, currency: string = 'SAR', decimals: number = 2): string {
  return `${cost.toFixed(decimals)} ${currency}`;
}

// ═══════════════════════════════════════════════════════════
// Export All
// ═══════════════════════════════════════════════════════════

export default {
  // Volume calculations
  calculateCylindricalVolume,
  calculateRectangularVolume,
  calculateSphericalVolume,
  calculateTankVolume,

  // Unit conversions
  litersToGallons,
  litersToM3,
  gallonsToLiters,
  m3ToLiters,
  convertVolume,

  // Percentage & level
  calculatePercentage,
  calculateLevelFromPercentage,
  calculateRemainingVolume,
  calculateUsedVolume,
  getTankStatus,

  // Refill
  calculateRefillAmount,
  calculateRefillToVolume,

  // Consumption & prediction
  calculateDailyConsumption,
  predictDaysUntilEmpty,
  predictEmptyDate,

  // Validation
  validateTankDimensions,
  validateReading,

  // Formatting
  formatVolume,
  formatPercentage,
  formatCost,

  // Constants
  CONVERSION_FACTORS,
};
